import { createStore } from "vuex"
import axios from "axios"
import router from "@/router"

// Feature modules
import ordersModule from "@/features/orders/store/orders.module"

const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL || "https://snkrroom-resell.ru/api/resell/v1"

const api = axios.create({
  baseURL: API_BASE_URL,
  paramsSerializer: {
    indexes: null, // Отправляет массивы как brand=value1&brand=value2 вместо brand[]=value1&brand[]=value2
  },
})

function withPhotoCacheBust(url) {
  if (!url || typeof url !== "string") return url
  try {
    const raw = typeof window !== "undefined"
      ? window.localStorage?.getItem("userPhotoCacheBust")
      : null
    const bust = Number(raw)
    if (!Number.isFinite(bust) || bust <= 0) return url
    const sep = url.includes("?") ? "&" : "?"
    // avoid duplicating if already present
    if (/[?&]v=\d+/.test(url)) return url
    return `${url}${sep}v=${bust}`
  } catch (e) {
    return url
  }
}

// ── Промежуточная корзина (заявка): кэш в localStorage ──
const BID_STORAGE_KEY = "citypro_bid_v1"

function loadBid() {
  try {
    const raw =
      typeof window !== "undefined"
        ? window.localStorage?.getItem(BID_STORAGE_KEY)
        : null
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function persistBid(bid) {
  try {
    window.localStorage?.setItem(BID_STORAGE_KEY, JSON.stringify(bid || []))
  } catch (e) {
    // приватный режим и т.п. — молча игнорируем
  }
}

api.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined" && window.Telegram.WebApp?.initData) {
      config.headers["WebAppInitData"] = window.Telegram.WebApp?.initData
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export default createStore({
  modules: {
    orders: ordersModule,
  },
  state: {
    data: {},
    user: {},
    myProducts: {
      items: [],
      total: 0,
    },
    myProductsCount: 0,
    notificationsCount: 0,
    userInfoUpdate: {
      type: "",
      delivery_time: 0,
      delivery_type: "",
    },
    favorites: {
      items: [],
      total: 0,
    },
    items: [],
    selectedModel: null,
    customModelMode: false,
    loading: false,
    requestsCount: 0,
    bootCompleted: false,
    bid: loadBid(),
    // {current: "seller", roles: [{role, active, registered}, ...]}
    userRoles: null,
    filtersModalOpen: false,
    sortModalOpen: false,
    marketViewState: null,
    // Общее число доступных пар в Маркете (из /market/catalog total_products)
    // — для плейсхолдера «Поиск по N товаров».
    marketTotalProducts: 0,
  },
  getters: {
    // Суммарное число пар в промежуточной корзине (для бейджа иконки)
    bidPairsCount: (state) =>
      (state.bid || []).reduce((n, i) => n + (i.quantity || 1), 0),
    // Модели, чьи пары уже в корзине (строка-ключи для сравнения)
    bidModelIds: (state) =>
      new Set((state.bid || []).map((i) => String(i.modelId))),
    // Товары (пары), уже добавленные в корзину
    bidProductIds: (state) =>
      new Set((state.bid || []).map((i) => String(i.id))),
  },
  mutations: {
    setData(state, data) {
      const next = data || {}
      if (next?.user?.photo_url) {
        next.user = {
          ...next.user,
          photo_url: withPhotoCacheBust(next.user.photo_url),
        }
      }
      state.data = next
    },
    setUser(state, data) {
      const next = data || {}
      if (next?.photo_url) {
        next.photo_url = withPhotoCacheBust(next.photo_url)
      }
      state.user = next
      // Смена активной роли — список ролей устарел, перезапросим
      if (state.userRoles && state.userRoles.current !== next.type) {
        state.userRoles = null
      }
    },
    changeUserInfoUpdate(state, payload) {
      const allowed = [
        "type",
        "username",
        "fullname",
        "delivery_time",
        "delivery_type",
      ]
      Object.entries(payload).forEach(([key, value]) => {
        if (allowed.includes(key) && value !== undefined) {
          state.userInfoUpdate[key] = value
        }
      })
      window.console.log(state.userInfoUpdate)
    },
    setFavorites(state, data) {
      state.favorites = data
    },
    setItems(state, data) {
      state.items = data
    },
    setMarketTotalProducts(state, count) {
      state.marketTotalProducts = Number(count) || 0
    },
    setSelectedModel(state, model) {
      state.selectedModel = model || null
    },
    // Режим «своя модель»: seller нажал «Добавить свою модель» в SelectModel,
    // вернулся на AddedProduct — там раскрывается блок полей модели.
    setCustomModelMode(state, value) {
      state.customModelMode = !!value
    },
    setMyProducts(state, payload) {
      state.myProducts = payload
    },
    setNotificationsCount(state, count) {
      state.notificationsCount = count
    },
    setProfileStats(state, stats) {
      if (stats) {
        if (stats.products_count !== undefined) {
          state.myProductsCount = stats.products_count
        }
        if (stats.favorites_count !== undefined) {
          state.favorites.total = stats.favorites_count
        }
        if (stats.notifications_unread !== undefined) {
          state.notificationsCount = stats.notifications_unread
        }
      }
    },
    setFiltersModalOpen(state, isOpen) {
      state.filtersModalOpen = isOpen
    },
    setMarketViewState(state, payload) {
      state.marketViewState = payload
    },
    setSortModalOpen(state, isOpen) {
      state.sortModalOpen = isOpen
    },
    START_LOADING(state) {
      state.requestsCount += 1
      state.loading = true
    },
    FINISH_LOADING(state) {
      state.requestsCount -= 1
      if (state.requestsCount <= 0) {
        state.requestsCount = 0
        state.loading = false
      }
    },
    setBootCompleted(state, value) {
      state.bootCompleted = value
    },
    addToBid(state, product) {
      // Проверяем, есть ли уже продукты в заявке
      if (state.bid.length > 0) {
        // Проверяем, что seller_id нового продукта совпадает с seller_id продуктов в заявке
        const firstBidItem = state.bid[0]
        if (
          firstBidItem.sellerId &&
          product.sellerId &&
          String(firstBidItem.sellerId) !== String(product.sellerId)
        ) {
          // Выбрасываем ошибку, чтобы компонент мог показать алерт
          throw new Error(
            "Вы можете добавлять в заявку только продукты одного и того же продавца"
          )
        }
      }

      // Проверяем, есть ли уже такой же продукт (с тем же id) в заявке
      const existingIndex = state.bid.findIndex(
        (item) => item.id === product.id
      )
      if (existingIndex === -1) {
        // Добавляем новый продукт
        state.bid.push({ ...product })
      } else {
        // Если продукт уже есть, увеличиваем количество, но не больше maxCount
        const currentQuantity = state.bid[existingIndex].quantity || 1
        const maxCount = state.bid[existingIndex].maxCount
        if (
          maxCount !== undefined &&
          maxCount !== null &&
          currentQuantity >= maxCount
        ) {
          // Уже достигнут максимум, не увеличиваем
          return
        }
        state.bid[existingIndex].quantity += 1
      }
      persistBid(state.bid)
    },
    updateProductQuantity(state, { productId, quantity }) {
      const productIndex = state.bid.findIndex((item) => item.id === productId)
      if (productIndex !== -1) {
        if (quantity <= 0) {
          state.bid.splice(productIndex, 1)
        } else {
          state.bid[productIndex].quantity = quantity
        }
      }
      persistBid(state.bid)
    },
    updateProductCustomPrice(state, { productId, customPrice }) {
      const productIndex = state.bid.findIndex((item) => item.id === productId)
      if (productIndex !== -1) {
        state.bid[productIndex].customPrice = customPrice
      }
      persistBid(state.bid)
    },
    removeFromBid(state, productId) {
      const index = state.bid.findIndex((item) => item.id === productId)
      if (index !== -1) {
        state.bid.splice(index, 1)
      }
      persistBid(state.bid)
    },
    updateBidQuantity(state, { productId, quantity }) {
      const index = state.bid.findIndex((item) => item.id === productId)
      if (index !== -1) {
        if (quantity <= 0) {
          state.bid.splice(index, 1)
        } else {
          const maxCount = state.bid[index].maxCount
          // Проверяем, не превышает ли новое количество максимально доступное
          if (
            maxCount !== undefined &&
            maxCount !== null &&
            quantity > maxCount
          ) {
            state.bid[index].quantity = maxCount
          } else {
            state.bid[index].quantity = quantity
          }
        }
      }
      persistBid(state.bid)
    },
    clearBid(state) {
      state.bid = []
      persistBid(state.bid)
    },
    setUserRoles(state, data) {
      state.userRoles = data || null
    },
  },
  actions: {
    async getUserRoles({ commit }) {
      const response = await api.get("/user/roles")
      commit("setUserRoles", response.data)
      return response
    },
    async switchUserRole({ dispatch }, { role }) {
      const response = await api.post("/user/roles/switch", { role })
      await dispatch("userMe")
      return response
    },
    async registerUserRole({ dispatch }, { role, ...payload }) {
      const response = await api.post("/user/roles/register", { role, ...payload })
      await dispatch("userMe")
      return response
    },
    async checkInitData({ commit }, token) {
      commit("START_LOADING")
      try {
        const response = await api.post("/check-init-data", token)
        commit("setData", response.data)
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
      } finally {
        commit("FINISH_LOADING")
      }
    },
    async userMe({ commit }) {
      commit("START_LOADING")

      try {
        const response = await api.get("/user/me")
        commit("setUser", response.data)
        if (response.data.is_activated === false) {
          router.push({ path: "/register" })

          setTimeout(() => {
            commit("FINISH_LOADING")
          }, 200)
        } else {
          commit("FINISH_LOADING")
        }
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
      }
    },
    async getProfile({ commit }) {
      try {
        const response = await api.get("/user/profile")
        const data = response.data || response

        // Обновляем user если он есть в ответе
        if (data.user) {
          commit("setUser", data.user)
        }

        // Обновляем stats
        if (data.stats) {
          commit("setProfileStats", data.stats)
        }

        return data
      } catch (error) {
        console.error("Ошибка при получении профиля:", error)
        return error
      }
    },
    async userDelete() {
      try {
        const response = await api.delete(`/user/delete`)
        return response
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async deleteUserPhoto() {
      try {
        const response = await api.delete(`/user/photo`)
        return response
      } catch (error) {
        console.error("Ошибка при удалении фото пользователя:", error)
        return error
      }
    },
    async getFavorites({ commit, state }, payload) {
      commit("START_LOADING")
      try {
        const response = await api.get("user/favorites", {
          params: {
            limit: payload?.limit ?? 20,
            offset: payload?.offset ?? 0,
          },
        })
        const newItems = response.data.items || []

        // Если offset = 0 или не указан, заменяем items, иначе добавляем к существующим
        if (payload?.offset === 0 || !payload?.offset) {
          commit("setFavorites", {
            items: newItems,
            total: response.data.total ?? 0,
          })
        } else {
          const currentItems = state.favorites?.items || []
          commit("setFavorites", {
            items: [...currentItems, ...newItems],
            total: response.data.total ?? state.favorites?.total ?? 0,
          })
        }
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      } finally {
        commit("FINISH_LOADING")
      }
    },
    async addFavorite({ dispatch }, payload) {
      try {
        const response = await api.post(`/favorite/${payload.product_id}/add`)
        dispatch("getFavorites", {
          limit: 20,
          offset: 0,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async deleteFavorite({ dispatch }, payload) {
      try {
        const response = await api.delete(
          `/favorite/${payload.product_id}/delete`
        )
        dispatch("getFavorites", {
          limit: 20,
          offset: 0,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async registerStart(context, payload) {
      try {
        return await api.post("/register/start", payload)
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        throw error
      }
    },
    async checkCode(context, code) {
      try {
        return await api.post(`/register/${code}/check-code`)
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async userUpdate(commit, payload) {
      try {
        if (payload.type === "reseller") {
          const form = new FormData()
          form.append("type", payload.type || "")
          if (payload.username != null && String(payload.username).trim() !== "")
            form.append("username", String(payload.username).trim())
          if (payload.fullname != null && String(payload.fullname).trim() !== "")
            form.append("fullname", String(payload.fullname).trim())
          if (payload.created_at != null && String(payload.created_at).trim() !== "")
            form.append("created_at", String(payload.created_at))
          return await api.patch(`/user/update`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        } else {
          // Backend expects form-data (multipart) for /user/update
          const form = new FormData()
          Object.entries(payload || {}).forEach(([key, value]) => {
            if (value === undefined) return
            if (value === null) {
              // Nullable fields: send empty string
              form.append(key, "")
              return
            }
            if (
              key === "photo" &&
              typeof File !== "undefined" &&
              value instanceof File
            ) {
              form.append("photo", value)
              return
            }
            form.append(key, String(value))
          })

          return await api.patch(`/user/update`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        }
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async updatePayment() {
      try {
        const response = await api.post("/user/update/payment")
        return response
      } catch (error) {
        console.error("Ошибка при обновлении платежной информации:", error)
        throw error
      }
    },
    async deletePayment() {
      try {
        const response = await api.post("/user/delete/payment")
        return response
      } catch (error) {
        console.error("Ошибка при удалении платежной информации:", error)
        throw error
      }
    },
    async getCategories() {
      try {
        const response = await api.get("/enums/product-model-category-type")
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async getBrands(context, payload) {
      try {
        const response = await api.get("/market/brands", {
          params: payload,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении брендов:", error)
        return error
      }
    },
    async getBrand(context, payload) {
      try {
        const params = {
          q: payload?.q,
          color: payload?.color,
          size: payload?.size,
          size_type: payload?.size_type,
          category: payload?.category,
          sort: payload?.sort,
          limit: payload?.limit,
          offset: payload?.offset,
          is_custom: payload?.is_custom,
        }

        Object.keys(params).forEach((key) => {
          // Для limit и offset сохраняем даже если они равны 0
          if (key === "limit" || key === "offset") {
            if (params[key] === undefined || params[key] === null) {
              delete params[key]
            }
            // Не удаляем, если значение 0
          } else if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === "" ||
            (typeof params[key] === "string" && !params[key].length)
          ) {
            delete params[key]
          }
        })

        const response = await api.get(`/market/brand/${payload.brand}/get`, {
          params,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении бренда:", error)
        return error
      }
    },
    async getMarketItems({ commit, state }, payload) {
      try {
        const response = await api.get("/market/catalog", {
          params: payload,
        })
        const newItems = response.data.items || []
        // Если offset = 0 или не указан, заменяем items, иначе добавляем к существующим
        if (payload.offset === 0 || !payload.offset) {
          commit("setItems", newItems)
        } else {
          const currentItems = state.items || []
          commit("setItems", [...currentItems, ...newItems])
        }
        // Общее число доступных пар — плейсхолдер «Поиск по N товаров»
        if (response.data.total_products !== undefined) {
          commit("setMarketTotalProducts", response.data.total_products)
        }
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async fetchModelsCatalog(context, payload = {}) {
      try {
        const params = {
          q: payload.q,
          brand: payload.brand,
          color: payload.color,
          size: payload.size,
          size_type: payload.size_type,
          category: payload.category,
          sort: payload.sort ?? "random",
          limit: payload.limit ?? 200,
          offset: payload.offset ?? 0,
        }

        Object.keys(params).forEach((key) => {
          if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === "" ||
            (typeof params[key] === "string" && !params[key].length)
          ) {
            delete params[key]
          }
        })

        const response = await api.get("/market/catalog", { params })
        return response.data || response
      } catch (error) {
        console.error("Ошибка при получении каталога моделей:", error)
        throw error
      }
    },
    async fetchModelList(context, payload = {}) {
      try {
        const params = {}

        if (payload.q) {
          params.q = payload.q
        }
        if (payload.brand) {
          params.brand = payload.brand
        }
        if (payload.category) {
          params.category = payload.category
        }
        if (payload.colour) {
          params.colour = payload.colour
        }
        if (payload.limit !== undefined) {
          params.limit = payload.limit
        }
        if (payload.offset !== undefined) {
          params.offset = payload.offset
        }

        const response = await api.get("/model/list", { params })
        return response.data || response
      } catch (error) {
        console.error("Ошибка при получении списка моделей:", error)
        throw error
      }
    },
    async getModel(context, payload) {
      try {
        const params = {
          sort: payload?.sort,
          limit: payload?.limit,
          offset: payload?.offset,
        }

        Object.keys(params).forEach((key) => {
          // Для limit и offset сохраняем даже если они равны 0
          if (key === "limit" || key === "offset") {
            if (params[key] === undefined || params[key] === null) {
              delete params[key]
            }
            // Не удаляем, если значение 0
          } else if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === ""
          ) {
            delete params[key]
          }
        })

        const response = await api.get(`/market/model/${payload.id}/get`, {
          params,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async getModelData(context, payload) {
      try {
        const response = await api.get(`/model/${payload.id}/get`)
        return response.data || response
      } catch (error) {
        console.error("Ошибка при получении данных модели:", error)
        throw error
      }
    },
    async getProduct(context, payload) {
      try {
        const response = await api.get(`/market/product/${payload.id}/get`)
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных:", error)
        return error
      }
    },
    async getSellerInfo(context, payload) {
      try {
        const params = {
          q: payload?.q,
          brand: payload?.brand,
          color: payload?.color,
          size: payload?.size,
          size_type: payload?.size_type,
          category: payload?.category,
          sort: payload?.sort,
          is_custom: payload?.is_custom,
          limit: payload?.limit,
          offset: payload?.offset,
        }

        Object.keys(params).forEach((key) => {
          if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === "" ||
            (typeof params[key] === "string" && !params[key].length)
          ) {
            delete params[key]
          }
        })

        const response = await api.get(`/market/seller/${payload.userId}/get`, {
          params,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных продавца:", error)
        return error
      }
    },
    async getMyProducts({ commit, state }, payload = {}) {
      try {
        const params = {
          limit: payload.limit ?? 100,
          offset: payload.offset ?? 0,
          sort: payload.sort ?? "date_desc",
        }

        Object.keys(params).forEach((key) => {
          if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === ""
          ) {
            delete params[key]
          }
        })

        const response = await api.get("/user/products", { params })
        const data = response.data || response
        const newItems = data.items || []

        // Если offset = 0 или не указан, заменяем items, иначе добавляем к существующим
        if (payload.offset === 0 || !payload.offset) {
          commit("setMyProducts", {
            items: newItems,
            total: data.total ?? 0,
          })
        } else {
          const currentItems = state.myProducts?.items || []
          commit("setMyProducts", {
            items: [...currentItems, ...newItems],
            total: data.total ?? state.myProducts?.total ?? 0,
          })
        }
        return data
      } catch (error) {
        console.error("Ошибка при получении товаров пользователя:", error)
        return error
      }
    },
    async addUserProduct(context, payload) {
      try {
        // Если payload это FormData, используем его напрямую
        // Axios автоматически установит Content-Type: multipart/form-data
        const config =
          payload instanceof FormData
            ? {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            : {}
        const response = await api.post("/product/add", payload, config)
        return response.data || response
      } catch (error) {
        console.error("Ошибка при добавлении товара:", error)
        throw error
      }
    },
    // Создание «своей модели» + товара одним запросом (POST /model/add).
    // Модель уходит в очередь модерации (product_model_requests), товар —
    // со status=pending и model_id=null; после апрува админом модель
    // переносится в публичный справочник, model_id заполняется.
    // 409 — артикул уже занят (в каталоге или в очереди заявок).
    async createCustomModel(context, payload) {
      const config =
        payload instanceof FormData
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : {}
      try {
        const response = await api.post("/model/add", payload, config)
        return response.data || response
      } catch (error) {
        console.error("Ошибка при создании своей модели:", error)
        throw error
      }
    },
    async updateProduct(context, { productId, payload }) {
      try {
        // Если payload это FormData, используем его напрямую
        // Axios автоматически установит Content-Type: multipart/form-data
        const config =
          payload instanceof FormData
            ? {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            : {}
        const response = await api.patch(
          `/product/${productId}/update`,
          payload,
          config
        )
        return response.data || response
      } catch (error) {
        console.error("Ошибка при обновлении товара:", error)
        throw error
      }
    },
    async getUserProduct(context, productId) {
      try {
        const response = await api.get(`/user/products/${productId}`)
        return response.data || response
      } catch (error) {
        console.error("Ошибка при получении продукта пользователя:", error)
        throw error
      }
    },
    async deleteProduct({ dispatch }, productId) {
      try {
        const response = await api.delete(`/product/${productId}/delete`)
        try {
          await dispatch("getProfile")
        } catch (e) {
          console.error("Не удалось обновить профиль после удаления", e)
        }
        return response.data || response
      } catch (error) {
        console.error("Ошибка при удалении товара:", error)
        throw error
      }
    },
    async uploadExcelCatalog({ dispatch }, formData) {
      try {
        const response = await api.post("/product/import-table", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        try {
          await dispatch("getProfile")
        } catch (e) {
          console.error("Не удалось обновить профиль после импорта", e)
        }
        return response.data || response
      } catch (error) {
        console.error("Ошибка при импорте Excel:", error)
        throw error
      }
    },
    async getSellers(context, payload) {
      try {
        const response = await api.get("/market/sellers", {
          params: payload,
        })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении списка селлеров:", error)
        return error
      }
    },
    async sendOffer(context, payload) {
      try {
        const response = await api.post("/reseller/offer/send", payload)
        return response.data || response
      } catch (error) {
        console.error("Ошибка при отправке заявки:", error)
        throw error
      }
    },
    async exportProductsTable(context, payload) {
      try {
        const response = await api.post("/product/export-table", payload)
        return response.data
      } catch (error) {
        console.error("Ошибка при экспорте товаров:", error)
        throw error
      }
    },
    async getUserOffers(context, payload = {}) {
      try {
        const params = {
          status: payload.status,
          sort: payload.sort || "random",
          limit: payload.limit || 10,
          offset: payload.offset || 0,
          brand: payload.brand,
          created_at: payload.created_at,
          created_at_from: payload.created_at_from,
          created_at_to: payload.created_at_to,
          type: payload.type,
        }

        Object.keys(params).forEach((key) => {
          if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === ""
          ) {
            delete params[key]
          }
        })

        const response = await api.get("/user/offers", { params })
        return response.data
      } catch (error) {
        console.error("Ошибка при получении сделок:", error)
        throw error
      }
    },
    async getDealDetails(context, dealId) {
      try {
        const response = await api.get(`/seller/deal/${dealId}/get`)
        return response.data
      } catch (error) {
        console.error("Ошибка при получении деталей сделки:", error)
        throw error
      }
    },
    async getOfferDetails(context, payload) {
      try {
        const response = await api.get(`/offer/${payload.id}/get`)
        return response.data
      } catch (error) {
        console.error("Ошибка при получении данных оффера:", error)
        throw error
      }
    },
    async getNotifications(context, payload = {}) {
      try {
        const params = {
          limit: payload.limit ?? 20,
          offset: payload.offset ?? 0,
        }
        const response = await api.get("/user/notifications", { params })
        const data = response.data || response

        return data
      } catch (error) {
        console.error("Ошибка при получении уведомлений:", error)
        throw error
      }
    },
    async markNotificationAsRead(context, payload) {
      try {
        const response = await api.post(
          `/user/notifications/${payload.id}/read`
        )
        return response.data || response
      } catch (error) {
        console.error("Ошибка при отметке уведомления:", error)
        throw error
      }
    },
    async markNotificationsAsRead(context, payload) {
      try {
        const ids = Array.isArray(payload?.ids) ? payload.ids : []
        if (!ids.length) return
        const response = await api.post("/notify/read", { ids })
        return response.data || response
      } catch (error) {
        console.error("Ошибка при отметке уведомлений:", error)
        throw error
      }
    },
  },
})
